import React, { useState } from 'react';
import { User, Lock, Shield, Save } from 'lucide-react';
import { PageHeader } from '@/components/shared/PageHeader';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Switch } from '@/components/ui/switch';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { toast } from 'sonner';

export const ProfilePage = () => {
  const [name, setName] = useState('John Admin');
  const [email, setEmail] = useState('john@propfirmhouse.com');
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [twoFAEnabled, setTwoFAEnabled] = useState(false);
  const [twoFACode, setTwoFACode] = useState('');

  const handleSaveProfile = () => {
    if (!name || !email) {
      toast.error('Name and email are required');
      return;
    }
    toast.success('Profile updated successfully');
  };

  const handleChangePassword = () => {
    if (!currentPassword || !newPassword || !confirmPassword) {
      toast.error('Please fill in all password fields');
      return;
    }
    
    if (newPassword !== confirmPassword) {
      toast.error('New passwords do not match');
      return;
    }
    
    if (newPassword.length < 8) {
      toast.error('Password must be at least 8 characters');
      return;
    }
    
    toast.success('Password changed successfully');
    setCurrentPassword('');
    setNewPassword('');
    setConfirmPassword('');
  };

  const handleToggle2FA = () => {
    if (!twoFAEnabled) {
      // Enabling 2FA
      toast.success('Scan the QR code with your authenticator app');
    } else {
      // Disabling 2FA
      toast.success('Two-factor authentication disabled');
    }
    setTwoFAEnabled(!twoFAEnabled);
  };

  const handleVerify2FA = () => {
    if (twoFACode.length !== 6) {
      toast.error('Please enter a 6-digit code');
      return;
    }
    toast.success('2FA verified and enabled!');
  };

  return (
    <div>
      <PageHeader
        title="Profile Settings"
        description="Manage your account settings and preferences"
        breadcrumbs={['Admin', 'Settings', 'Profile']}
      />

      <div className="grid gap-6 lg:grid-cols-3">
        {/* Profile Card */}
        <Card className="rounded-xl border-slate-200 shadow-sm">
          <CardHeader className="text-center">
            <Avatar className="h-24 w-24 mx-auto mb-4">
              <AvatarImage src="https://api.dicebear.com/7.x/avataaars/svg?seed=John" />
              <AvatarFallback className="text-2xl">JA</AvatarFallback>
            </Avatar>
            <CardTitle>{name}</CardTitle>
            <CardDescription>{email}</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-2 text-sm">
              <div className="flex justify-between py-2 border-t">
                <span className="text-slate-600">Role</span>
                <span className="font-medium">Super Admin</span>
              </div>
              <div className="flex justify-between py-2 border-t">
                <span className="text-slate-600">Joined</span>
                <span className="font-medium">Jan 2025</span>
              </div>
              <div className="flex justify-between py-2 border-t">
                <span className="text-slate-600">2FA Status</span>
                <span className={`font-medium ${twoFAEnabled ? 'text-emerald-600' : 'text-slate-400'}`}>
                  {twoFAEnabled ? 'Enabled' : 'Disabled'}
                </span>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Settings Tabs */}
        <div className="lg:col-span-2">
          <Tabs defaultValue="profile" className="space-y-6">
            <TabsList>
              <TabsTrigger value="profile">
                <User className="h-4 w-4 mr-2" />
                Profile
              </TabsTrigger>
              <TabsTrigger value="password">
                <Lock className="h-4 w-4 mr-2" />
                Password
              </TabsTrigger>
              <TabsTrigger value="2fa">
                <Shield className="h-4 w-4 mr-2" />
                Two-Factor Auth
              </TabsTrigger>
            </TabsList>

            <TabsContent value="profile">
              <Card className="rounded-xl border-slate-200 shadow-sm">
                <CardHeader>
                  <CardTitle>Profile Information</CardTitle>
                  <CardDescription>Update your account details</CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div>
                    <Label>Full Name</Label>
                    <Input
                      value={name}
                      onChange={(e) => setName(e.target.value)}
                      className="mt-2"
                    />
                  </div>
                  
                  <div>
                    <Label>Email Address</Label>
                    <Input
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      className="mt-2"
                    />
                  </div>
                  
                  <div>
                    <Label>Profile Picture URL</Label>
                    <Input
                      placeholder="https://example.com/avatar.jpg"
                      className="mt-2"
                    />
                  </div>
                  
                  <Button onClick={handleSaveProfile}>
                    <Save className="h-4 w-4 mr-2" />
                    Save Changes
                  </Button>
                </CardContent>
              </Card>
            </TabsContent>

            <TabsContent value="password">
              <Card className="rounded-xl border-slate-200 shadow-sm">
                <CardHeader>
                  <CardTitle>Change Password</CardTitle>
                  <CardDescription>Ensure your account is using a strong password</CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div>
                    <Label>Current Password</Label>
                    <Input
                      type="password"
                      placeholder="••••••••"
                      value={currentPassword}
                      onChange={(e) => setCurrentPassword(e.target.value)}
                      className="mt-2"
                    />
                  </div>
                  
                  <div>
                    <Label>New Password</Label>
                    <Input
                      type="password"
                      placeholder="••••••••"
                      value={newPassword}
                      onChange={(e) => setNewPassword(e.target.value)}
                      className="mt-2"
                    />
                    <p className="text-xs text-slate-500 mt-1">
                      Must be at least 8 characters
                    </p>
                  </div>
                  
                  <div>
                    <Label>Confirm New Password</Label>
                    <Input
                      type="password"
                      placeholder="••••••••"
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      className="mt-2"
                    />
                  </div>
                  
                  <Button onClick={handleChangePassword}>
                    <Lock className="h-4 w-4 mr-2" />
                    Update Password
                  </Button>
                </CardContent>
              </Card>
            </TabsContent>

            <TabsContent value="2fa">
              <Card className="rounded-xl border-slate-200 shadow-sm">
                <CardHeader>
                  <CardTitle>Two-Factor Authentication</CardTitle>
                  <CardDescription>
                    Add an extra layer of security to your account
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-6">
                  <div className="flex items-center justify-between p-4 bg-slate-50 rounded-lg">
                    <div>
                      <p className="font-medium text-slate-900">2FA Status</p>
                      <p className="text-sm text-slate-600">
                        {twoFAEnabled ? 'Enabled' : 'Disabled'}
                      </p>
                    </div>
                    <Switch
                      checked={twoFAEnabled}
                      onCheckedChange={handleToggle2FA}
                    />
                  </div>

                  {twoFAEnabled && (
                    <div className="space-y-4">
                      <div className="text-center p-6 bg-white border-2 border-dashed border-slate-200 rounded-lg">
                        <div className="h-48 w-48 mx-auto bg-slate-100 rounded-lg flex items-center justify-center mb-4">
                          <p className="text-xs text-slate-500">QR Code Placeholder</p>
                        </div>
                        <p className="text-sm text-slate-600 mb-2">
                          Scan this QR code with your authenticator app
                        </p>
                        <code className="text-xs bg-slate-100 px-3 py-1 rounded">
                          ABCD-EFGH-IJKL-MNOP
                        </code>
                      </div>

                      <div>
                        <Label>Verification Code</Label>
                        <Input
                          placeholder="000000"
                          maxLength={6}
                          value={twoFACode}
                          onChange={(e) => setTwoFACode(e.target.value.replace(/\D/g, ''))}
                          className="mt-2 text-center text-lg tracking-widest"
                        />
                      </div>

                      <Button onClick={handleVerify2FA} className="w-full">
                        <Shield className="h-4 w-4 mr-2" />
                        Verify & Complete Setup
                      </Button>
                    </div>
                  )}

                  {!twoFAEnabled && (
                    <div className="text-center py-6">
                      <Shield className="h-12 w-12 text-slate-300 mx-auto mb-3" />
                      <p className="text-sm text-slate-600">
                        Enable two-factor authentication to secure your account
                      </p>
                    </div>
                  )}
                </CardContent>
              </Card>
            </TabsContent>
          </Tabs>
        </div>
      </div>
    </div>
  );
};
